import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import debounce from 'lodash/debounce';
import Collapse from 'react-collapse';

import Button from 'components/Button';
import Icon from 'components/Icon';

import Map from 'components/Map';
import DepartmentMarker from 'components/DepartmentMarker';

import { track } from 'tracking';

import css from './DepartmentInformationPage.module.scss';
import DepartmentInformationBillingUnit from './DepartmentInformationBillingUnit';

export const VIEW_TYPES = {
    full: 'Full',
    list: 'List'
};

class DepartmentInformationPage extends React.Component {
    state = {
        isExpanded: this.props.isBillingUnitsListExpanded,
        isDesktop: true,
        isBillingUnitsListExpanded: this.props.isBillingUnitsListExpanded
    };

    static propTypes = {
        heading: PropTypes.string,
        internalUrl: PropTypes.string,
        view: PropTypes.oneOf(Object.values(VIEW_TYPES)).isRequired,
        readMoreEHF: PropTypes.string,
        phone: PropTypes.string,
        emailAddress: PropTypes.string,
        streetAddressLine1: PropTypes.string,
        streetAddressLine2: PropTypes.string,
        postalAddressLine1: PropTypes.string,
        postalAddressLine2: PropTypes.string,
        mapTitle: PropTypes.string,
        location: PropTypes.string,
        zoomLevel: PropTypes.number,
        externalUrl: PropTypes.string,
        style: PropTypes.object,
        phoneAndEmailLabel: PropTypes.string.isRequired,
        streetAddressLabel: PropTypes.string.isRequired,
        postalAddressLabel: PropTypes.string.isRequired,
        invoiceLabel: PropTypes.string.isRequired,
        orgNoLabel: PropTypes.string.isRequired,
        invoiceEhfLabel: PropTypes.string.isRequired,
        invoiceEhfLinkText: PropTypes.string.isRequired,
        sendEmailLabel: PropTypes.string,
        sendInvoiceEmailLabel: PropTypes.string,
        tileLayer: PropTypes.string,
        lang: PropTypes.shape({
            showMoreLabel: PropTypes.string.isRequired,
            showLessLabel: PropTypes.string.isRequired,
            invoiceAsEHFNoText: PropTypes.string.isRequired,
            allContactPersonsLinkText: PropTypes.string.isRequired,
        }).isRequired,
        billingUnits: PropTypes.arrayOf(PropTypes.shape(DepartmentInformationBillingUnit.propTypes)),
        businessAreas: PropTypes.arrayOf(PropTypes.string),
        allContactPersonsUrl: PropTypes.string,
        isBillingUnitsListExpanded: PropTypes.bool
    };

    static defaultProps = {
        theme: 0,
        view: "List",
    }

    componentDidUpdate(prevProps) {
        if (
            prevProps.isBillingUnitsListExpanded !== this.props.isBillingUnitsListExpanded &&
            this.props.isBillingUnitsListExpanded !== undefined
        ) {
            this.setState({
                isExpanded: this.props.isBillingUnitsListExpanded,
                isBillingUnitsListExpanded: this.props.isBillingUnitsListExpanded
            });
        }
    }

    toggleExpand = () => {
        this.setState({isExpanded: !this.state.isExpanded}, () => {
            track('list_expansion', this.props.heading);
        });
    }

    toggleBillingsExpand = e => {
        e.preventDefault();
        this.setState({isBillingUnitsListExpanded: !this.state.isBillingUnitsListExpanded});
    }

    getIsDesktop = () => {
        this.setState({ isDesktop: (typeof window !== 'undefined') && window.matchMedia('(min-width: 1024px)').matches });
    }

    debouncedGetIsDesktop = debounce(this.getIsDesktop, 500);

    componentDidMount() {
        this.getIsDesktop();

        (typeof window !== 'undefined') && window.addEventListener('resize', this.debouncedGetIsDesktop);
    }

    componentWillUnmount() {
        (typeof window !== 'undefined') && window.removeEventListener('resize', this.debouncedGetIsDesktop);
    }

    render() {
        const {
            readMoreEHF,
            heading,
            internalUrl,
            phone,
            emailAddress,
            streetAddressLine1,
            streetAddressLine2,
            postalAddressLine1,
            postalAddressLine2,
            mapTitle,
            location,
            zoomLevel,
            externalUrl,
            style,
            view,
            phoneAndEmailLabel,
            streetAddressLabel,
            postalAddressLabel,
            invoiceLabel,
            orgNoLabel,
            invoiceEhfLabel,
            invoiceEhfLinkText,
            sendEmailLabel,
            sendInvoiceEmailLabel,
            tileLayer,
            lang,
            billingUnits,
            allContactPersonsUrl,
        } = this.props;

        const {
            isBillingUnitsListExpanded, 
            isExpanded,
            isDesktop
        } = this.state;

        const classNames = cn(css.block, {
            [css.list]: view === VIEW_TYPES.list,
            [css.full]: view === VIEW_TYPES.full,
            [css.yellow]: isExpanded && view !== VIEW_TYPES.full,
        });

        const expandedContent = cn(css.businessInfo);

        const expandableContent = [
            <div key="visitationaddress" className={cn(css.visitationAdress, {
                [css.noMobileSpace]: !(postalAddressLine1 || postalAddressLine2)
            })}
            >
                <h4 className={cn({
                    [css.invisibleHeader]: !(streetAddressLine1 || streetAddressLine2) 
                })}
                >{streetAddressLabel}</h4>
                {streetAddressLine1 &&<p>{streetAddressLine1}</p>}
                {streetAddressLine2 &&<p>{streetAddressLine2}</p>}
            </div>,
            <div key="postaddress" className={cn(css.postAdress, {
                [css.noMobileSpace]: !(postalAddressLine1 || postalAddressLine2)
            })}
            >
                <h4 className={cn({
                    [css.invisibleHeader]: !(postalAddressLine1 || postalAddressLine2) 
                })}
                >{postalAddressLabel}</h4>
                {postalAddressLine1 && <p>{postalAddressLine1}</p>}
                {postalAddressLine2 && <p>{postalAddressLine2}</p>}
            </div>,
            billingUnits && billingUnits.length > 0 ? (
                <div 
                    key="invoice" 
                    className={cn(css.invoice, {
                        [css.noMobileSpace]: true,
                        [css.noMobileSpace]: !(billingUnits[0].billingName || billingUnits[0].organizationNumber || billingUnits[0].invoiceEmailAddress)
                    })}
                >
                    {invoiceLabel && <h4>{invoiceLabel}</h4>}

                    {billingUnits.length > 1 ?
                        <button 
                            onClick={this.toggleBillingsExpand}
                            className={css['toggle-billing-units-button']}

                        >
                            <Icon 
                                icon={isBillingUnitsListExpanded  ? "minus" : "plus"}
                                className={css['toggle-billing-units-button-icon']}
                            />
                            <span className={css['toggle-billing-units-button-text']}>{isBillingUnitsListExpanded ? lang.showLessLabel : lang.showMoreLabel}  </span>
                        </button>
                        :
                        <DepartmentInformationBillingUnit
                            organizationNumber={billingUnits[0].organizationNumber}
                            invoiceAsEHF={billingUnits[0].invoiceAsEHF}
                            invoiceEmailAddress={billingUnits[0].invoiceEmailAddress}
                            billingName={billingUnits[0].billingName}
                            invoiceEhfLabel={invoiceEhfLabel}
                            orgNoLabel={orgNoLabel}
                            readMoreEHF={readMoreEHF}
                            invoiceEhfLinkText={invoiceEhfLinkText}
                            heading={heading}
                            sendInvoiceEmailLabel={sendInvoiceEmailLabel}
                            invoiceAsEHFNoText={lang.invoiceAsEHFNoText}
                            invoiceAsEHFYesText={lang.invoiceAsEHFYesText}
                        />
                    }
                </div>
            ) : null
        ];

        const coordinates = location && location.split(",").map(parseFloat);

        return (
            <div className={classNames} style={style}>
                {view === VIEW_TYPES.full && ([
                    mapTitle && <h2 className={css.contactHeading} key="contact">{mapTitle}</h2>,
                    location && (
                        <div className={css.map} key="map">
                            <Map center={coordinates} tileLayer={tileLayer} zoomLevel={zoomLevel}>
                                <DepartmentMarker position={coordinates} />
                            </Map>
                        </div>
                    )
                ])}
                <div className={expandedContent}>
                    <div className={css.businessInfoWrapper}>
                        <h3 className={css.heading}>
                            {externalUrl || internalUrl ?  
                                <a href={externalUrl ? externalUrl : internalUrl}>
                                    {heading}
                                    {externalUrl && <Icon icon="share" className={cn(css.inlineIcon, css['external-icon'])} />}
                                </a> 
                                : 
                                heading
                            }
                        </h3>
                        <div className={css.expandableContent}>
                            <div className={cn(css.contactInfo, {
                                [css.noMobileSpace]: !(phone || emailAddress)
                            })}
                            >
                                <h4 className={cn({
                                    [css.invisibleHeader]: !(phone || emailAddress) 
                                })}
                                >{phoneAndEmailLabel}</h4>

                                {phone &&<p><a data-name={heading} href={`tel:${phone}`}>{phone}</a></p>}
                                {emailAddress &&<p><a data-name={heading} href={`mailto:${emailAddress}`}>{sendEmailLabel || emailAddress}</a></p>}
                                {allContactPersonsUrl && <p><a href={allContactPersonsUrl}>{lang.allContactPersonsLinkText}</a></p>}
                            </div>
                            {isDesktop
                                ? expandableContent
                                : (
                                    <Collapse isOpened={isExpanded}>
                                        {expandableContent}
                                    </Collapse>
                                )
                            }
                        </div>
                        {billingUnits && billingUnits.length > 1 && 
                        
                            <Collapse 
                                isOpened={
                                    isDesktop ? 
                                        isBillingUnitsListExpanded :
                                        isBillingUnitsListExpanded && isExpanded
                                }
                            >
                                <div className={css.expandableBillingContent}>
                                    {billingUnits.map((billing, i) => (
                                        <div key={i} className={css['billing-column']}>
                                            <DepartmentInformationBillingUnit 
                                                billingName={billing.billingName}
                                                organizationNumber={billing.organizationNumber}
                                                invoiceAsEHF={billing.invoiceAsEHF}
                                                invoiceEmailAddress={billing.invoiceEmailAddress}
                                                invoiceEhfLabel={invoiceEhfLabel}
                                                orgNoLabel={orgNoLabel}
                                                readMoreEHF={readMoreEHF}
                                                invoiceEhfLinkText={invoiceEhfLinkText}
                                                heading={heading}
                                                invoiceAsEHFNoText={lang.invoiceAsEHFNoText}
                                                invoiceAsEHFYesText={lang.invoiceAsEHFYesText}
                                                sendInvoiceEmailLabel={sendInvoiceEmailLabel}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </Collapse>
                        }
                        
                        <div className={css.toggler}>
                            <Button
                                className={css.togglerButton}
                                onClick={this.toggleExpand}
                                icon={isExpanded  ? "close" : "zoom-in"}
                                isActive={isExpanded}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default DepartmentInformationPage;



// WEBPACK FOOTER //
// ./src/components/DepartmentInformationPage/DepartmentInformationPage.jsx